/**
 * Calculate contract status based on contract history periods
 * @param {Array} contractHistory - Array of contract periods
 * @param {boolean} isCompanyActive - Whether the company is active (default: true)
 * @returns {Object} - { status, className, icon, daysRemaining, expirationDate, description }
 */
export const getContractStatus = (contractHistory = [], isCompanyActive = true) => {
  // Se a empresa estiver inativa, retorna status "Inativa" independente do contrato
  if (isCompanyActive === false) {
    return {
      status: 'Inativa',
      className: 'status-inactive',
      icon: '🔴',
      daysRemaining: null,
      expirationDate: null,
      description: 'Empresa desativada no sistema',
    };
  }

  if (!contractHistory || contractHistory.length === 0) {
    return {
      status: 'Sem Contrato',
      className: 'status-no-contract',
      icon: '⚪',
      daysRemaining: null,
      expirationDate: null,
      description: 'Nenhum período definido',
    };
  }

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Find active period (current date is between start_date and end_date)
  const activePeriod = contractHistory.find((period) => {
    if (!period.end_date) {
      // Ongoing contract without end date
      return period.start_date <= todayStr;
    }
    return period.start_date <= todayStr && period.end_date >= todayStr;
  });

  if (activePeriod) {
    if (!activePeriod.end_date) {
      // Contract is ongoing (no end date)
      return {
        status: 'Em Andamento',
        className: 'status-ongoing',
        icon: '🔵',
        daysRemaining: null,
        expirationDate: null,
        description: 'Contrato sem prazo definido',
      };
    }

    // Calculate days remaining
    const endDate = new Date(activePeriod.end_date + 'T00:00:00');
    const diffTime = endDate - today;
    const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (daysRemaining <= 30) {
      // Expiring soon (30 days or less) - amarelo
      return {
        status: 'Expirando',
        className: 'status-expiring',
        icon: '🟡',
        daysRemaining,
        expirationDate: activePeriod.end_date,
        description: `Expira em ${daysRemaining} ${daysRemaining === 1 ? 'dia' : 'dias'}`,
      };
    }

    // Active contract - verde
    return {
      status: 'Ativo',
      className: 'status-active',
      icon: '🟢',
      daysRemaining,
      expirationDate: activePeriod.end_date,
      description: `Vence em ${daysRemaining} ${daysRemaining === 1 ? 'dia' : 'dias'}`,
    };
  }

  // No active period found - check if there's an expired period
  const sortedPeriods = contractHistory
    .filter((p) => p.end_date)
    .sort((a, b) => b.end_date.localeCompare(a.end_date));

  if (sortedPeriods.length > 0) {
    const lastPeriod = sortedPeriods[0];
    if (lastPeriod.end_date < todayStr) {
      // Contract expired - laranja
      const endDate = new Date(lastPeriod.end_date + 'T00:00:00');
      const diffTime = today - endDate;
      const daysExpired = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      return {
        status: 'Expirado',
        className: 'status-expired',
        icon: '🟠',
        daysRemaining: -daysExpired,
        expirationDate: lastPeriod.end_date,
        description: `Expirado há ${daysExpired} ${daysExpired === 1 ? 'dia' : 'dias'}`,
      };
    }
  }

  // No contract defined
  return {
    status: 'Sem Contrato',
    className: 'status-no-contract',
    icon: '⚪',
    daysRemaining: null,
    expirationDate: null,
    description: 'Nenhum período definido',
  };
};

/**
 * Format date from ISO (YYYY-MM-DD) to Brazilian format (DD/MM/YYYY)
 * @param {string} isoDate - Date in ISO format
 * @returns {string} - Date in DD/MM/YYYY format
 */
export const formatDateBR = (isoDate) => {
  if (!isoDate) return '-';
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};
